import { useCallback, useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchRules,
  createRule,
  updateRule,
  deleteRule,
  testRule,
  CATEGORIES,
  SEVERITIES,
  ACTIONS,
  AM_STR as S,
  type ModRule,
} from './autoModApi'

export function AutoModRulesScreen() {
  const { toast } = useToast()
  const [rows, setRows] = useState<ModRule[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  // create form
  const [pattern, setPattern] = useState('')
  const [isRegex, setIsRegex] = useState(false)
  const [category, setCategory] = useState('spam')
  const [severity, setSeverity] = useState('medium')
  const [action, setAction] = useState('flag')
  const [busy, setBusy] = useState(false)
  // test
  const [sample, setSample] = useState('')
  const [testResult, setTestResult] = useState<boolean | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchRules(ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const add = async () => {
    if (!pattern.trim()) return
    setBusy(true)
    try {
      await createRule({ pattern: pattern.trim(), is_regex: isRegex, category, severity, action })
      setPattern('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const toggle = async (r: ModRule) => {
    try {
      await updateRule(r.id, { enabled: !r.enabled })
      setRows((prev) => prev?.map((x) => (x.id === r.id ? { ...x, enabled: !x.enabled } : x)) ?? prev)
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const del = async (id: number) => {
    try {
      await deleteRule(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const runTest = async () => {
    if (!pattern.trim()) return
    try {
      const { matched } = await testRule(pattern.trim(), isRegex, sample)
      setTestResult(matched)
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.rules} screenId="A6.29" description={S.rulesDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl space-y-4">
          {/* Create + test */}
          <div className="space-y-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={pattern}
                onChange={(e) => {
                  setPattern(e.target.value)
                  setTestResult(null)
                }}
                placeholder={S.pattern}
                className="min-w-[200px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
              />
              <label className="flex items-center gap-1 text-xs text-secondary">
                <input type="checkbox" checked={isRegex} onChange={(e) => setIsRegex(e.target.checked)} />
                {S.regex}
              </label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary">
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary">
                {SEVERITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select value={action} onChange={(e) => setAction(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary">
                {ACTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <button type="button" onClick={add} disabled={busy || !pattern.trim()} className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50">
                <Plus size={14} />
                {S.add}
              </button>
            </div>
            {/* Test box */}
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={sample}
                onChange={(e) => {
                  setSample(e.target.value)
                  setTestResult(null)
                }}
                placeholder={S.testSample}
                className="min-w-[200px] flex-1 rounded-md border border-dashed border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-accent"
              />
              <button type="button" onClick={runTest} disabled={!pattern.trim()} className="rounded-md border border-border-default px-3 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50">
                {S.test}
              </button>
              {testResult !== null && (
                <span className={`text-sm ${testResult ? 'text-success' : 'text-tertiary'}`}>
                  {testResult ? S.matched : S.noMatch}
                </span>
              )}
            </div>
          </div>

          {/* Rules list */}
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              {rows.map((r, i) => (
                <div key={r.id} className={`flex items-center gap-3 px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={r.enabled}
                    onClick={() => toggle(r)}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${r.enabled ? 'bg-accent' : 'bg-surface-3'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${r.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-sm text-primary">
                      {r.is_regex && <span className="mr-1 text-xs text-accent">/re/</span>}
                      {r.pattern}
                    </p>
                    <p className="text-xs text-tertiary">
                      {r.category} · {r.severity} · {r.action}
                      {r.flags > 0 && ` · ${S.matches}: ${r.flags}`}
                    </p>
                  </div>
                  <button type="button" onClick={() => del(r.id)} className="shrink-0 rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
